import { homeKeys } from '@/features/home/queries/query-keys';
import { fetchCoachByUid } from '@/features/home/services/get-coach';
import { useSessionSelectPlayerStore } from '@/features/player-session/stores/session-select-player-store';
import { useSessionStore } from '@/stores';
import { useQueryClient } from '@tanstack/react-query';
import type { User } from 'firebase/auth';
import { getAuth, onAuthStateChanged, onIdTokenChanged } from 'firebase/auth';
import { useEffect, useEffectEvent } from 'react';

import { getFirebaseApp } from '../init.web';

const auth = () => getAuth(getFirebaseApp());

export const AuthStateEffect = () => {
  const queryClient = useQueryClient();
  const {
    resetSessionState,
    setSessionUser,
    setSessionAuthToken,
    setSessionInitialized,
    setIsAuthenticated,
  } = useSessionStore();

  const handleAuthStateChanged = useEffectEvent(async (user: User | null) => {
    if (!user) {
      resetSessionState();
      useSessionSelectPlayerStore.getState().reset();
      return;
    }
    const token = await user.getIdToken();
    setSessionAuthToken(token);
    if (user.uid) {
      await queryClient.fetchQuery({
        queryKey: homeKeys.coach(user.uid),
        queryFn: () => fetchCoachByUid(user.uid),
      });
    }
    setSessionUser(user);
    setIsAuthenticated(!!user);
    setSessionInitialized(true);
  });

  const handleIdTokenChanged = useEffectEvent(async (user: User | null) => {
    if (!user) {
      return;
    }
    const token = await user.getIdToken();
    setSessionAuthToken(token);
  });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth(), handleAuthStateChanged);
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onIdTokenChanged(auth(), handleIdTokenChanged);
    return unsub;
  }, []);

  return null;
};
